import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/components/AuthProvider';
import OptimizedImage from '@/components/OptimizedImage';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from '@/components/ui/dialog';
import { Drawer, DrawerContent, DrawerDescription, DrawerHeader, DrawerTitle, DrawerTrigger } from '@/components/ui/drawer';
import { ScrollArea } from '@/components/ui/scroll-area';
import { ExpandableText } from '@/components/ui/expandable-text';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  MapPin, 
  Users, 
  Gauge, 
  Calendar, 
  Clock, 
  Phone, 
  Mail, 
  MessageCircle,
  Heart,
  Share2,
  AlertTriangle,
  FileText,
  Download,
  Eye,
  ExternalLink,
  Info,
  ChevronRight,
  Star,
  CheckCircle,
  Award,
  TrendingUp,
  Sparkles,
  Home,
  Upload,
  DollarSign
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useFavorites } from '@/hooks/useFavorites';
import type { HelicopterWithDetails, Option } from '@/types/helicopter';
import OptionIcon from '@/components/OptionIcon';

import { UniversalMap } from '@/components/maps';
import StandardizedIcon from '@/components/wizard/StandardizedIcon';
import { getCategoryIcon } from '@/utils/iconMapping';
import { cn } from '@/lib/utils';

// Helper component to render category icons
const CategoryIconDisplay = ({ category, className }: { category: string; className?: string }) => {
  const Icon = getCategoryIcon(category);
  return <Icon className={className} />;
};

// Import professional components
import ProfessionalPhotoGallery from '@/components/listing/ProfessionalPhotoGallery';
import ProfessionalOptionsDisplay from '@/components/listing/ProfessionalOptionsDisplay';
import SaleWizardDataDisplay from '@/components/listing/SaleWizardDataDisplay';
import ServiceIconsDisplay from '@/components/listing/ServiceIconsDisplay';
import BuyRequestFieldsDisplay from '@/components/listing/BuyRequestFieldsDisplay';
import RentDetailsDisplay from '@/components/listing/RentDetailsDisplay';
import WetLeaseDetailsDisplay from '@/components/listing/WetLeaseDetailsDisplay';
import DryLeaseDetailsDisplay from '@/components/listing/DryLeaseDetailsDisplay';
import { useUnifiedContact } from '@/contexts/UnifiedContactContext';
import { useViewTracking } from '@/hooks/useViewTracking';
import { useSharedUserPreferences } from '@/contexts/UserPreferencesContext';
import { useIsDesktop } from '@/hooks/use-tablet';
import { useEffectiveVisibility } from '@/hooks/useEffectiveVisibility';
import { formatListingPrice } from '@/utils/currencyConverter';
import { formatSpeed, formatDistance } from '@/utils/unitConverter';
import { extractAircraftDetails } from '@/utils/listingDataTransform';
import { SetHeroImageModal } from '@/components/listing/SetHeroImageModal';
import { ManagePhotosModal } from '@/components/listing/ManagePhotosModal';
import { ShareListingButton } from '@/components/ShareListingButton';
import { BlurredPrivateData } from '@/components/privacy/BlurredPrivateData';
import { SellerSection } from '@/components/listing/SellerSection';
// Import the helicopter icons from wizard
import helicopterMainPhotoIcon from '@/assets/helicopter-main-photo-icon.png';
import helicopterFrontViewIcon from '@/assets/helicopter-front-view-icon.png';
import helicopterSideViewIcon from '@/assets/helicopter-side-view-icon.png';
import helicopterCockpitIcon from '@/assets/helicopter-cockpit-icon.png';
import helicopterInteriorIcon from '@/assets/helicopter-interior-icon.png';
import helicopterCameraIcon from '@/assets/helicopter-camera-icon.png';
import helicopterVideoIcon from '@/assets/helicopter-video-icon.png';

interface ListingDocument {
  id: string;
  document_category: string;
  document_name: string;
  file_url: string;
  verification_status: string;
  is_required: boolean;
  is_confidential: boolean;
  created_at: string;
}

interface ListingMedia {
  id: string;
  media_type: string;
  file_url: string;
  display_order?: number;
  metadata?: any;
}

interface ListingWithFullDetails {
  id: string;
  title: string;
  description: string;
  price_usd: number | null;
  listing_type: string;
  condition: string;
  year_manufactured?: number;
  total_flight_hours?: number;
  location: string;
  latitude?: number;
  longitude?: number;
  status: string;
  featured: boolean;
  images?: string[];
  included_services?: any[];
  operational_requirements?: any;
  lease_terms?: any;
  lease_type?: string;
  minimum_lease_period?: number;
  maximum_lease_period?: number;
  is_buy_request?: boolean;
  budget_min?: number | null;
  budget_max?: number | null;
  hero_image?: string | null;
  created_at: string;
  updated_at: string;
  user_id: string;
  visibility_settings?: {
    use_global_defaults?: boolean;
    hide_price?: boolean;
    hide_location?: boolean;
    hide_serial?: boolean;
    hide_hours?: boolean;
    hide_contact?: boolean;
  };
  helicopters: {
    id: string;
    manufacturer: string;
    model: string;
    seating: number;
    speed_kmh?: number;
    range_km?: number;
    eco_score?: number;
    icon_url?: string;
  };
  profiles: {
    id: string;
    display_name?: string;
    avatar_url?: string;
    company_name?: string;
    user_type?: string;
    bio?: string;
    phone?: string;
    location?: string;
    is_verified?: boolean;
    response_time_rating?: number;
    dealer_status?: string;
    verification_documents_count?: number;
  } | null;
  media?: ListingMedia[];
  documents?: ListingDocument[];
  selectedOptions?: Option[];
  mediaByCategory?: Record<string, ListingMedia[]>;
}

interface ListingDetailModalProps {
  listingId: string;
  trigger: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  mockData?: {
    listing: any;
    media: any[];
    documents: any[];
    selectedOptions: any[];
  };
}

const ListingDetailModal: React.FC<ListingDetailModalProps> = ({
  listingId,
  trigger,
  open,
  onOpenChange,
  mockData
}) => {
  const [listing, setListing] = useState<ListingWithFullDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [humanAddress, setHumanAddress] = useState<string | null>(null);
  const [heroImageModalOpen, setHeroImageModalOpen] = useState(false);
  const [managePhotosModalOpen, setManagePhotosModalOpen] = useState(false);
  const [confidentialDocuments, setConfidentialDocuments] = useState<any[]>([]);
  const { openUnifiedContact } = useUnifiedContact();
  const { preferences } = useSharedUserPreferences();
  const isDesktop = useIsDesktop();
  const isMobileOrTablet = !isDesktop;
  // For Drawer vs Dialog, use isMobileOrTablet; for UI affordances, use isMobile alias
  const isMobile = isMobileOrTablet;
  const { isFavorite, toggleFavorite } = useFavorites();
  const { user } = useAuth();
  
  const handleFavoriteToggle = async () => {
    if (listing?.id) {
      await toggleFavorite(listing.id, 'helicopter');
    }
  };

  const isControlled = open !== undefined;
  const [internalOpen, setInternalOpen] = useState(false);
  const effectiveOpen = isControlled ? open : internalOpen;

  // Refs for scroll containers (mobile div, desktop ScrollArea)
  const mobileScrollRef = useRef<HTMLDivElement | null>(null);
  const desktopScrollRef = useRef<HTMLDivElement | null>(null);

  // Reset scroll to top whenever the modal opens, the listing changes, or
  // content finishes loading (so images/sections don't leave us mid-scroll).
  useEffect(() => {
    if (!effectiveOpen) return;
    const resetScroll = () => {
      if (mobileScrollRef.current) {
        mobileScrollRef.current.scrollTop = 0;
      }
      if (desktopScrollRef.current) {
        const viewport = desktopScrollRef.current.querySelector<HTMLElement>('[data-radix-scroll-area-viewport]');
        if (viewport) viewport.scrollTop = 0;
      }
    };
    // Double rAF: wait for the post-load layout to commit, then reset.
    const raf1 = requestAnimationFrame(() => {
      resetScroll();
      const raf2 = requestAnimationFrame(resetScroll);
      (resetScroll as any)._raf2 = raf2;
    });
    const t = setTimeout(resetScroll, 0);
    const t2 = setTimeout(resetScroll, 150);
    return () => {
      cancelAnimationFrame(raf1);
      if ((resetScroll as any)._raf2) cancelAnimationFrame((resetScroll as any)._raf2);
      clearTimeout(t);
      clearTimeout(t2);
    };
  }, [effectiveOpen, listingId, listing?.id, loading]);

  // Track views when modal opens
  useViewTracking(listingId, effectiveOpen);
  
  // Use grant-aware visibility
  const {
    blurPrice: shouldHidePrice,
    blurLocation: shouldHideLocation,
    blurHours: shouldHideHours,
    blurSerial: shouldHideSerial,
    blurContact: shouldHideContact,
  } = useEffectiveVisibility(listing?.id, listing?.visibility_settings, listing?.user_id);
  
  const isOwner = user?.id === listing?.user_id;

  // Compute hidden fields object for InfoRequestModal
  const hiddenFields = {
    price: shouldHidePrice || false,
    location: shouldHideLocation || false,
    serial: shouldHideSerial || false,
    hours: shouldHideHours || false,
    contact: shouldHideContact || false,
  };

  // Helper function to open unified contact modal with preselected field
  // Closes the detail modal first to avoid nesting issues
  const requestField = (field: string) => {
    if (!listing) return;
    if (!user?.id) {
      window.dispatchEvent(new CustomEvent('require-auth', {
        detail: { message: 'Please sign in to request this information' }
      }));
      return;
    }
    handleOpenChange(false);
    openUnifiedContact({
      listingId: listing.id,
      listingTitle: listing.title,
      sellerId: listing.user_id,
      assetType: 'helicopter',
      hiddenFields,
      confidentialDocuments,
      preselectedFields: [field],
    });
  };

  // Helper function to open unified contact modal for general contact
  const openContactSeller = () => {
    if (!listing) return;
    if (!user?.id) {
      window.dispatchEvent(new CustomEvent('require-auth', {
        detail: { message: 'Please sign in to contact this seller' }
      }));
      return;
    }
    handleOpenChange(false);
    openUnifiedContact({
      listingId: listing.id,
      listingTitle: listing.title,
      sellerId: listing.user_id,
      assetType: 'helicopter',
      hiddenFields,
      confidentialDocuments,
    });
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (isControlled) {
      onOpenChange?.(newOpen);
    } else {
      setInternalOpen(newOpen);
    }
  };

  // Helper to format condition labels (especially for buy requests which use minCondition)
  const formatConditionLabel = (condition?: string, isBuyRequest?: boolean): string => {
    if (!condition) return '';
    const labels: Record<string, string> = {
      'new': 'New',
      'excellent': 'Excellent',
      'good': 'Good or Better',
      'fair': 'Fair or Better',
      'serviceable': 'Serviceable'
    };
    const formatted = labels[condition.toLowerCase()] || condition;
    return isBuyRequest ? `Min: ${formatted}` : formatted;
  };

  // Helper function to group media by category
  const groupMediaByCategory = (mediaData: any[]): Record<string, ListingMedia[]> => {
    const mediaByCategory: Record<string, ListingMedia[]> = {};
    mediaData?.forEach(media => {
      const metadata = media.metadata as any;
      const category = metadata?.category || 'additional';
      if (!mediaByCategory[category]) {
        mediaByCategory[category] = [];
      }
      mediaByCategory[category].push(media);
    });
    return mediaByCategory;
  };

  useEffect(() => {
    if (effectiveOpen && listingId) {
      if (mockData) {
        // Use mock data for preview mode
        const processedMockData = {
          ...mockData.listing,
          media: mockData.media || [],
          documents: mockData.documents || [],
          selectedOptions: mockData.selectedOptions || [],
          mediaByCategory: groupMediaByCategory(mockData.media || []),
          helicopters: mockData.listing.helicopter || mockData.listing.helicopters
        };
        setListing(processedMockData as unknown as ListingWithFullDetails);
        setLoading(false);
      } else {
        fetchListingDetails();
      }
    }
  }, [effectiveOpen, listingId, mockData]);

  const fetchListingDetails = async () => {
    try {
      setLoading(true);
      
      // Fetch listing with helicopter and profile details
      const { data: listingData, error: listingError } = await supabase
        .from('helicopter_listings')
        .select(`
          *,
          helicopters!helicopter_listings_helicopter_id_fkey (
            id,
            manufacturer,
            model,
            seating,
            speed_kmh,
            range_km,
            eco_score,
            icon_url
          )
        `)
        .eq('id', listingId)
        .maybeSingle();

      if (listingError || !listingData) {
        throw new Error(listingError?.message || 'Listing not found');
      }

      // Fetch seller profile separately (more reliable than FK hint)
      const { data: profileData } = await supabase
        .from('profiles')
        .select('id, user_id, display_name, avatar_url, company_name, company_logo_url, represent_as_company, user_type, is_verified, created_at, dealer_status')
        .eq('user_id', listingData.user_id)
        .maybeSingle();

      // Fetch media files
      const { data: mediaData } = await supabase
        .from('listing_media')
        .select('*')
        .eq('listing_id', listingId)
        .order('display_order', { ascending: true });

      // Fetch documents using edge function for secure access
      let documentsData: any[] = [];
      try {
        const { data: session } = await supabase.auth.getSession();
        const response = await fetch('https://dvawogvuaomipakwlvst.supabase.co/functions/v1/get-listing-documents', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(session?.session?.access_token && {
              'Authorization': `Bearer ${session.session.access_token}`
            })
          },
          body: JSON.stringify({ listingId, assetType: 'helicopter' })
        });
        
        if (response.ok) {
          const result = await response.json();
          documentsData = result.documents || [];
          // Store confidential docs for the unified modal
          setConfidentialDocuments(
            documentsData
              .filter((d: any) => d.is_confidential)
              .map((d: any) => ({
                id: d.id,
                document_name: d.document_name,
                document_category: d.document_category,
                has_access: d.has_access
              }))
          );
        }
      } catch (docError) {
        console.error('Error fetching documents via edge function:', docError);
        // Fallback to direct fetch (will only work for owners)
        const { data: fallbackDocs } = await supabase
          .from('listing_documents')
          .select('*')
          .eq('listing_id', listingId)
          .order('created_at', { ascending: false });
        documentsData = fallbackDocs || [];
      }

      // Fetch selected options from operational_requirements
      const operationalReqs = listingData.operational_requirements as any;
      const selectedOptionsIds = operationalReqs?.selectedOptions || [];
      let selectedOptionsData: Option[] = [];
      
      // Use mockData selectedOptions if available (preview mode), otherwise fetch from DB
      if (mockData?.selectedOptions) {
        selectedOptionsData = mockData.selectedOptions;
      } else if (Array.isArray(selectedOptionsIds) && selectedOptionsIds.length > 0) {
        const { data: optionsData } = await supabase
          .from('v_options_with_icons')
          .select('*')
          .in('id', selectedOptionsIds);
        
        selectedOptionsData = optionsData || [];
      }

      // Group media by category
      const mediaByCategory = groupMediaByCategory(mediaData || []);

      // Check if helicopter and profile data is valid
      if (!listingData.helicopters || ('error' in listingData.helicopters)) {
        throw new Error('Helicopter data not found or corrupted');
      }

      const validListing = {
        ...listingData,
        profiles: profileData || null,
        included_services: Array.isArray(listingData.included_services) ? listingData.included_services : [],
        media: mediaData || [],
        documents: documentsData || [],
        selectedOptions: selectedOptionsData,
        mediaByCategory,
        helicopters: listingData.helicopters
      };

      setListing(validListing as unknown as ListingWithFullDetails);
    } catch (error) {
      console.error('Error fetching listing details:', error);
    } finally {
      setLoading(false);
    }
  };

  // Reverse geocode coordinates to human-readable address for header
  useEffect(() => {
    const geocode = async () => {
      try {
        if (!listing?.latitude || !listing?.longitude) return;
        
        const { reverseGeocode } = await import('@/utils/reverseGeocode');
        const result = await reverseGeocode(listing.latitude, listing.longitude);
        
        if (result.success) {
          setHumanAddress(result.address);
        } else {
          console.debug('Geocoding failed, using coordinates');
        }
      } catch (e) {
        console.error('Geocoding error:', e);
      }
    };
    geocode();
  }, [listing?.latitude, listing?.longitude]);

  const formatPrice = (amount: number, type: string) => {
    return formatListingPrice(amount, type, preferences?.preferred_currency || 'USD');
  };

  if (loading) {
    const Wrapper = isMobile ? Drawer : Dialog;
    const Trigger = isMobile ? DrawerTrigger : DialogTrigger;
    const Content = isMobile ? DrawerContent : DialogContent;
    
    return (
      <Wrapper open={isControlled ? effectiveOpen : undefined} onOpenChange={handleOpenChange}>
        <Trigger asChild>{trigger}</Trigger>
        <Content className={isMobile ? "h-[50vh]" : "max-w-4xl max-h-[90vh] overflow-y-auto"}>
          {isMobile ? (
            <>
              <DrawerTitle className="sr-only">Loading...</DrawerTitle>
              <DrawerDescription className="sr-only">Loading listing details</DrawerDescription>
            </>
          ) : (
            <>
              <DialogTitle className="sr-only">Loading...</DialogTitle>
              <DialogDescription className="sr-only">Loading listing details</DialogDescription>
            </>
          )}
          {isMobile && <div className="mx-auto mt-2 h-1.5 w-12 rounded-full bg-muted mb-2" />}
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        </Content>
      </Wrapper>
    );
  }

  if (!listing) {
    const Wrapper = isMobile ? Drawer : Dialog;
    const Trigger = isMobile ? DrawerTrigger : DialogTrigger;
    const Content = isMobile ? DrawerContent : DialogContent;
    
    return (
      <Wrapper open={isControlled ? effectiveOpen : undefined} onOpenChange={handleOpenChange}>
        <Trigger asChild>{trigger}</Trigger>
        <Content className={isMobile ? "h-[50vh]" : ""}>
          {isMobile ? (
            <>
              <DrawerTitle className="sr-only">Error</DrawerTitle>
              <DrawerDescription className="sr-only">Unable to load listing</DrawerDescription>
            </>
          ) : (
            <>
              <DialogTitle className="sr-only">Error</DialogTitle>
              <DialogDescription className="sr-only">Unable to load listing</DialogDescription>
            </>
          )}
          {isMobile && <div className="mx-auto mt-2 h-1.5 w-12 rounded-full bg-muted mb-2" />}
          <div className="text-center py-8">
            <AlertTriangle className="h-12 w-12 mx-auto text-red-500 mb-4" />
            <p>Unable to load listing details.</p>
          </div>
        </Content>
      </Wrapper>
    );
  }

  // Helper functions
  const getListingTypeLabel = (type: string, isBuyRequest?: boolean, leaseType?: string) => {
    if (isBuyRequest) {
      if (leaseType === 'dry_lease') return 'Wanted Dry Lease';
      if (leaseType === 'wet_lease') return 'Wanted Wet Lease';
      return 'Wanted to Buy';
    }
    
    const labels: Record<string, string> = {
      sale: 'For Sale',
      rent: 'For Rent',
      dry_lease: 'Dry Lease',
      wet_lease: 'Wet Lease',
      buy: 'Wanted to Buy',
      wanted: 'Wanted to Buy'
    };
    return labels[type] || type;
  };


  const categorizeOptions = (options: Option[]) => {
    const categories = {};
    options.forEach(option => {
      if (!categories[option.category]) {
        categories[option.category] = [];
      }
      categories[option.category].push(option);
    });
    return categories;
  };

  const premiumCategories = ['Safety', 'Avionics', 'Medical'];
  const categorizedOptions = listing.selectedOptions ? categorizeOptions(listing.selectedOptions) : {};
  const premiumOptions = listing.selectedOptions?.filter(opt => premiumCategories.includes(opt.category)) || [];
  
  // Extract wizard data for header display
  const aircraftDetails = extractAircraftDetails(listing);

  // Photo category mapping for icons
  const photoCategoryIcons = {
    mainPhoto: helicopterMainPhotoIcon,
    frontView: helicopterFrontViewIcon,
    rightSide: helicopterSideViewIcon,
    leftSide: helicopterSideViewIcon,
    cockpit: helicopterCockpitIcon,
    interior: helicopterInteriorIcon,
    additional: helicopterCameraIcon,
    videos: helicopterVideoIcon
  };

  const photoCategoryLabels = {
    mainPhoto: 'Hero Shot',
    frontView: 'Front View',
    rightSide: 'Right Side',
    leftSide: 'Left Side', 
    cockpit: 'Cockpit',
    interior: 'Interior',
    additional: 'Additional',
    videos: 'Videos'
  };

  const heroImageUrl = (() => {
    const mainPhoto = (listing.media || []).find(m => m.media_type === 'photo' && m.metadata?.category === 'mainPhoto');
    if (mainPhoto) return mainPhoto.file_url;
    const firstPhoto = (listing.media || []).find(m => m.media_type === 'photo');
    if (firstPhoto) return firstPhoto.file_url;
    return listing.images && listing.images.length > 0 ? listing.images[0] : undefined;
  })();

  // Mobile version with Drawer
  if (isMobile) {
    return (
      <Drawer open={isControlled ? effectiveOpen : undefined} onOpenChange={handleOpenChange} handleOnly={true}>
        <DrawerTrigger asChild>{trigger}</DrawerTrigger>
        <DrawerContent className="h-[100svh] max-h-[100svh] flex flex-col z-[100]" showCloseButton={false} showHandle={false}>
          {/* Sticky Back Button Header - flush at top */}
          <div className="sticky top-0 z-50 bg-background border-b border-border px-3 py-2.5 flex items-center gap-3 flex-shrink-0">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => handleOpenChange(false)}
              className="h-9 w-9 flex-shrink-0 hover:bg-muted"
            >
              <ChevronRight className="h-5 w-5 rotate-180" />
            </Button>
            <span className="font-medium text-sm text-foreground/80 truncate flex-1">Back to Marketplace</span>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="icon"
                className="h-9 w-9"
                onClick={handleFavoriteToggle}
                aria-label={listing && isFavorite(listing.id) ? 'Remove from favorites' : 'Add to favorites'}
              >
                <Heart className={cn("w-4 h-4", listing && isFavorite(listing.id) && "fill-red-500 text-red-500")} />
              </Button>
              {listing && (
                <ShareListingButton
                  listingId={listing.id}
                  title={listing.title}
                  price={listing.price_usd || 0}
                  location={listing.location}
                  listingType={listing.listing_type}
                  variant="ghost"
                  size="icon"
                  className="h-9 w-9"
                />
              )}
            </div>
          </div>
          
          <div ref={mobileScrollRef} className="flex-1 min-h-0 overflow-y-auto overscroll-contain">
            <div className="px-4 pb-32 overflow-x-hidden">
              {/* Header section - Mobile optimized */}
              <div className="pt-4 pb-4 space-y-5">
                  
                  {/* Title Section */}
                  <div className="bg-muted/30 rounded-lg border border-border p-3">
                    <div className="flex items-center gap-2">
                      {/* Icon */}
                      <div className="w-10 h-10 rounded-full bg-background flex items-center justify-center border border-border shadow-sm flex-shrink-0">
                        <StandardizedIcon 
                          src={listing.helicopters.icon_url ? `${listing.helicopters.icon_url}?v=${Date.now()}` : helicopterFrontViewIcon} 
                          alt={`${listing.helicopters.manufacturer} ${listing.helicopters.model} icon`} 
                          size="sm" 
                          className="text-muted-foreground"
                        />
                      </div>
                      
                      {/* Title and badges */}
                      <div className="flex-1 min-w-0">
                        <DrawerTitle className="text-xl font-semibold text-foreground mb-2">
                          {listing.helicopters.manufacturer} {listing.helicopters.model}
                        </DrawerTitle>
                        <DrawerDescription className="sr-only">
                          {getListingTypeLabel(listing.listing_type, listing.is_buy_request, listing.lease_type)} listing for {listing.helicopters.manufacturer} {listing.helicopters.model}
                        </DrawerDescription>
                        
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <Badge variant="outline" className="text-xs px-2 py-0.5 font-normal">
                            {getListingTypeLabel(listing.listing_type, listing.is_buy_request, listing.lease_type)}
                          </Badge>
                          <Badge variant="outline" className="text-xs px-2 py-0.5 font-normal">
                            {formatConditionLabel(listing.condition, listing.is_buy_request)}
                          </Badge>
                          {(listing as any).configuration_name && (
                            <Badge 
                              variant="outline" 
                              className="text-sm px-3 py-1.5 font-semibold bg-gradient-to-r from-primary/15 to-primary/5 border-2 border-primary/30 text-primary flex items-center gap-1.5 shadow-sm"
                            >
                              <Sparkles className="w-4 h-4" />
                              {(listing as any).configuration_name}
                            </Badge>
                          )}
                          {listing.featured && (
                            <span className="text-xs text-muted-foreground flex items-center gap-1">
                              <Star className="w-3 h-3 fill-current" />
                              Featured
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {/* Title and Description */}
                    {listing.title && (
                      <div className="mt-2 text-foreground text-base font-medium">
                        {listing.title}
                      </div>
                    )}
                    {listing.description && (
                      <ExpandableText
                        text={listing.description}
                        clampLines={3}
                        className="mt-1"
                      />
                    )}
                  </div>

                  {/* Seller Section */}
                  {listing.profiles && (
                    <SellerSection profile={listing.profiles} className="mt-4" onNavigate={() => handleOpenChange(false)} />
                  )}

                  {/* Metadata Card - properly contained */}
                  <div className="bg-background rounded-xl border border-border p-4 shadow-sm space-y-3">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <MapPin className="w-4 h-4 text-muted-foreground/60 flex-shrink-0" />
                      {shouldHideLocation ? (
                        <BlurredPrivateData
                          placeholderContent={<span className="font-medium">City, Country</span>}
                          fieldName="Location"
                          size="sm"
                          inline
                          onRequestInfo={() => requestField('location')}
                        />
                      ) : (
                        <span className="font-medium break-words">{humanAddress || listing.location}</span>
                      )}
                    </div>
                    
                    <div className="flex items-center gap-4 text-sm text-muted-foreground flex-wrap">
                      {/* For buy requests: show Max Age; for sale: show year */}
                      {listing.is_buy_request ? (
                        (listing as any).max_age && (
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4 text-muted-foreground/60 flex-shrink-0" />
                            <span>Up to {(listing as any).max_age} years old</span>
                          </div>
                        )
                      ) : (
                        (aircraftDetails.year ?? listing.year_manufactured) && (
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4 text-muted-foreground/60 flex-shrink-0" />
                            <span>{aircraftDetails.year ?? listing.year_manufactured}</span>
                          </div>
                        )
                      )}
                      
                      {/* For buy requests: show Max Flight Hours; for sale: show total hours */}
                      {listing.is_buy_request ? (
                        (listing as any).max_flight_hours && (
                          <div className="flex items-center gap-2">
                            <Clock className="w-4 h-4 text-muted-foreground/60 flex-shrink-0" />
                            <span>Max {Number((listing as any).max_flight_hours).toLocaleString()} hrs</span>
                          </div>
                        )
                      ) : (
                        (aircraftDetails.totalFlightHours ?? listing.total_flight_hours) && (
                          <div className="flex items-center gap-2">
                            <Clock className="w-4 h-4 text-muted-foreground/60 flex-shrink-0" />
                            {shouldHideHours ? (
                              <BlurredPrivateData
                                placeholderContent={<span>X,XXX hrs</span>}
                                fieldName="Hours"
                                size="sm"
                                inline
                                onRequestInfo={() => requestField('hours')}
                              />
                            ) : (
                              <span>{(aircraftDetails.totalFlightHours ?? listing.total_flight_hours).toLocaleString()} hrs</span>
                            )}
                          </div>
                        )
                      )}
                    </div>
                  </div>

                  {/* Price & Actions Section */}
                  <div className="bg-muted/50 rounded-lg border border-border p-3">
                    {/* Price/Budget section */}
                    <div className="mb-3">
                      <p className="text-xs text-muted-foreground mb-1">{listing.is_buy_request ? 'Budget' : 'Price'}</p>
                      {shouldHidePrice ? (
                        <BlurredPrivateData
                          placeholderContent={<span className="text-2xl font-semibold text-foreground">$X,XXX,XXX</span>}
                          fieldName="Price"
                          size="lg"
                          onRequestInfo={() => requestField('price')}
                        />
                      ) : (
                        <p className="text-2xl font-semibold text-foreground">
                          {listing.is_buy_request ? (
                            (() => {
                              const min = listing.budget_min;
                              const max = listing.budget_max;
                              if (min && max) return `${formatPrice(min, listing.listing_type)} - ${formatPrice(max, listing.listing_type)}`;
                              if (min) return `From ${formatPrice(min, listing.listing_type)}`;
                              if (max) return `Up to ${formatPrice(max, listing.listing_type)}`;
                              return 'Budget not specified';
                            })()
                          ) : (
                            listing.price_usd != null ? formatPrice(listing.price_usd, listing.listing_type) : 'Price on request'
                          )}
                        </p>
                      )}
                    </div>
                    
                    {/* Actions section - stacked for mobile */}
                    <div className="space-y-2">
                      <Button 
                        className={cn("w-full", isOwner ? "bg-muted text-muted-foreground cursor-not-allowed hover:bg-muted" : "bg-foreground hover:bg-foreground/90 text-background")}
                        onClick={isOwner ? undefined : openContactSeller}
                        disabled={isOwner}
                      >
                        <MessageCircle className="w-4 h-4 mr-2" />
                        {isOwner ? 'This is your listing' : listing.is_buy_request ? 'Contact Buyer' : 'Contact Seller'}
                      </Button>
                      
                      {listing.is_buy_request && (
                      <Button 
                        variant="outline"
                        className="w-full"
                        onClick={(e) => {
                          e.stopPropagation();
                          const event = new CustomEvent('open-buy-request-response', { detail: { request: listing } });
                          window.dispatchEvent(event);
                        }}
                      >
                          <DollarSign className="w-4 h-4 mr-2" />
                          Offer Your Helicopter
                        </Button>
                      )}
                      
                      <div className="flex gap-2">
                        <Button 
                           variant="outline" 
                           size="icon" 
                           className="flex-1"
                          onClick={handleFavoriteToggle}
                          aria-label={listing && isFavorite(listing.id) ? 'Remove from favorites' : 'Add to favorites'}
                         >
                          <Heart className={cn("w-4 h-4", listing && isFavorite(listing.id) && "fill-red-500 text-red-500")} />
                        </Button>
                        
                        {listing && (
                          <ShareListingButton
                            listingId={listing.id}
                            title={listing.title}
                            price={listing.price_usd || 0}
                            location={listing.location}
                            listingType={listing.listing_type}
                            variant="outline"
                            size="icon"
                           className="flex-1"
                          />
                        )}
                      </div>
                    </div>
                  </div>

                </div>

              {/* Content area - Mobile optimized */}
              <div className="space-y-5">
                {/* Professional Photo Gallery */}
                {listing.is_buy_request && heroImageUrl ? (
                  <Card className="overflow-hidden">
                   <div className="aspect-[16/10] relative bg-muted">
                      <OptimizedImage
                        src={heroImageUrl}
                        alt={`${listing.helicopters.manufacturer} ${listing.helicopters.model} hero image`}
                        size="full"
                        priority
                        className="w-full h-full object-contain"
                      />
                    </div>
                    <CardContent className="p-3">
                      <p className="text-sm text-muted-foreground">Hero Image</p>
                    </CardContent>
                  </Card>
                ) : (
                  <ProfessionalPhotoGallery 
                    media={listing.media || []} 
                    legacyImages={listing.images || []}
                    canManagePhotos={user?.id === listing.user_id && !listing.is_buy_request}
                    onAddPhotos={() => setManagePhotosModalOpen(true)}
                  />
                )}

                {/* Premium Features Section */}
                {premiumOptions.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2 text-lg">
                        <Sparkles className="w-5 h-5 text-yellow-500" />
                        Premium Features
                      </CardTitle>
                      <p className="text-sm text-muted-foreground">
                        High-value equipment that significantly enhances market value
                      </p>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-1 gap-3">
                         {premiumOptions.map((option) => (
                           <div key={option.id} className="premium-option-badge">
                             <OptionIcon option={option} size={16} showLabel={false} />
                             <div className="min-w-0 flex-1">
                              <div className="font-medium text-sm break-words">{option.name}</div>
                              {option.value_add_percent && (
                                 <div className="text-xs text-green-600 dark:text-green-400">+{option.value_add_percent}%</div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Categorized Equipment & Options */}
                {listing.selectedOptions && listing.selectedOptions.length > 0 && (
                  <Card>
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <div>
                          <CardTitle className="text-lg">Equipment & Options</CardTitle>
                          <p className="text-sm text-muted-foreground mt-1">
                            {listing.selectedOptions.length} items included
                          </p>
                        </div>
                        <Badge variant="secondary" className="text-lg">
                          {listing.selectedOptions.length}
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent>
                        <Tabs defaultValue={Object.keys(categorizedOptions)[0]} className="w-full">
                        <div className="relative">
                          <div className="overflow-x-auto -mx-2 px-2 scrollbar-hide">
                          <TabsList className="w-max sm:w-full flex h-auto gap-2 bg-muted/50 p-1 after:content-[''] after:w-4 after:flex-shrink-0">
                            {Object.keys(categorizedOptions).map((category) => (
                              <TabsTrigger 
                                key={category} 
                                value={category}
                                className="flex items-center gap-1.5 text-xs sm:text-sm px-2.5 py-1.5 whitespace-nowrap min-w-0"
                              >
                                <CategoryIconDisplay category={category} className="w-4 h-4" />
                                <span className="hidden sm:inline">{category}</span>
                                <span className="sm:hidden">{category.split(' ')[0]}</span>
                                <span className="text-muted-foreground">({categorizedOptions[category].length})</span>
                              </TabsTrigger>
                            ))}
                          </TabsList>
                          </div>
                          <div className="absolute right-0 top-0 bottom-0 w-6 bg-gradient-to-l from-background to-transparent pointer-events-none sm:hidden" />
                        </div>
                        {Object.entries(categorizedOptions).map(([category, options]: [string, Option[]]) => (
                          <TabsContent key={category} value={category} className="mt-4">
                            <div className="options-category-grid">
                              {options.map((option) => (
                                <div key={option.id} className="option-item-compact">
                                  <OptionIcon option={option} size={16} showLabel={false} />
                                  <div className="min-w-0 flex-1">
                                    <div className="text-sm font-medium break-words">{option.name}</div>
                                    {option.description && (
                                      <div className="text-xs text-muted-foreground line-clamp-2">
                                        {option.description}
                                      </div>
                                    )}
                                    {option.value_add_percent && (
                                      <Badge variant="secondary" className="text-xs mt-1">
                                        +{option.value_add_percent}% value
                                      </Badge>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </TabsContent>
                        ))}
                      </Tabs>
                    </CardContent>
                  </Card>
                )}

                {/* Listing-Type Specific Components */}
                {listing.is_buy_request && (
                  <BuyRequestFieldsDisplay
                    budgetMin={listing.budget_min ?? undefined}
                    budgetMax={listing.budget_max ?? undefined}
                    financingStatus={(listing as any).financing_status}
                    urgencyLevel={(listing as any).urgency_level}
                    preferredDeliveryDate={(listing as any).preferred_delivery_date}
                    modelPreferences={(listing as any).model_preferences}
                    requiredEquipment={(listing as any).required_equipment}
                    downPayment={(listing as any).down_payment}
                    loanTerm={(listing as any).loan_term}
                    maxAge={(listing as any).max_age}
                    maxFlightHours={(listing as any).max_flight_hours}
                    operationalCoverage={(listing.operational_requirements as any)?.operationalCoverage}
                    searchRadius={(listing as any).search_radius}
                    bestTimeToCall={(listing as any).best_time_to_call}
                    insuranceCapability={(listing.operational_requirements as any)?.insuranceCapability}
                    pilotQualifications={(listing.operational_requirements as any)?.pilotQualifications}
                    serviceLevel={(listing.operational_requirements as any)?.serviceLevel || (listing.operational_requirements as any)?.wetLeaseTerms?.serviceLevel}
                    responseTime={(listing.operational_requirements as any)?.responseTime || (listing.operational_requirements as any)?.wetLeaseTerms?.responseTime}
                    operatingHours={(listing.operational_requirements as any)?.operatingHours || (listing.operational_requirements as any)?.buyRequestDetails?.operatingHours}
                    intendedUse={(listing.operational_requirements as any)?.intendedUse || (listing.operational_requirements as any)?.buyRequestDetails?.intendedUse}
                    minimumBilling={(listing.operational_requirements as any)?.minimumBilling || (listing.operational_requirements as any)?.buyRequestDetails?.minimumBilling}
                    preferredContactMethods={(listing as any).preferred_contact_methods || []}
                    requestedServices={(listing.included_services as any) || (listing.operational_requirements as any)?.selectedServices || []}
                    // NEW: Buy wizard fields
                    budgetFlexibility={(listing.operational_requirements as any)?.buyRequestDetails?.budgetFlexibility}
                    preApprovalAmount={(listing.operational_requirements as any)?.buyRequestDetails?.preApprovalAmount}
                    preferredDeliveryMethod={(listing.operational_requirements as any)?.buyRequestDetails?.preferredDeliveryMethod}
                    tradeInAircraft={(listing.operational_requirements as any)?.buyRequestDetails?.tradeInAircraft}
                    // Min condition and configuration
                    minCondition={(listing as any).condition}
                    configurationName={(listing as any).configuration_name}
                  />
                )}

                {listing.listing_type === 'rent' && (
                  <RentDetailsDisplay
                    hourlyRate={(listing as any).hourly_rate}
                    dailyRate={(listing as any).daily_rate}
                    minimumRentalPeriod={(listing as any).minimum_rental_period}
                    securityDeposit={(listing as any).security_deposit}
                    pilotRequirements={(listing as any).pilot_requirements}
                    insuranceRequirements={(listing as any).insurance_requirements}
                    geographicRestrictions={(listing as any).geographic_restrictions}
                    weatherMinimums={(listing as any).weather_minimums}
                    checkoutRequirements={(listing as any).checkout_requirements}
                    hidePrice={shouldHidePrice}
                    onRequestInfo={() => requestField('price')}
                  />
                )}

                {(listing.listing_type === 'dry_lease' || listing.listing_type === 'wet_lease') && (
                  <ServiceIconsDisplay
                    services={listing.included_services}
                    leaseType={listing.lease_type as 'dry' | 'wet'}
                    crewQualifications={(listing.operational_requirements as any)?.crewQualifications}
                    maintenanceResponsibility={(listing.lease_terms as any)?.maintenanceResponsibility}
                    minimumLeasePeriod={listing.minimum_lease_period}
                    maximumLeasePeriod={listing.maximum_lease_period}
                    positioningCost={(listing.lease_terms as any)?.positioningCost}
                  />
                )}

                {listing.listing_type === 'wet_lease' && (
                  <WetLeaseDetailsDisplay
                    hourlyRate={(listing.lease_terms as any)?.hourlyRate}
                    monthlyRate={(listing.lease_terms as any)?.monthlyRate}
                    depositRequired={(listing.lease_terms as any)?.depositRequired}
                    minimumPeriod={(listing.lease_terms as any)?.minimumPeriod || listing.minimum_lease_period}
                    maximumPeriod={(listing.lease_terms as any)?.maximumPeriod || listing.maximum_lease_period}
                    crewQualifications={(listing.operational_requirements as any)?.crewQualifications || (listing.lease_terms as any)?.wetLeaseTerms?.crewQualifications}
                    standbyRates={(listing.operational_requirements as any)?.standbyRates || (listing.lease_terms as any)?.wetLeaseTerms?.standbyRates}
                    positioningCosts={(listing.operational_requirements as any)?.positioningCosts || (listing.lease_terms as any)?.wetLeaseTerms?.positioningCosts}
                    minimumBilling={(listing.operational_requirements as any)?.minimumBilling || (listing.lease_terms as any)?.wetLeaseTerms?.minimumBilling}
                    operationalControl={(listing.operational_requirements as any)?.operationalControl || (listing.lease_terms as any)?.wetLeaseTerms?.operationalControl}
                    thirdPartyOperations={(listing.operational_requirements as any)?.thirdPartyOperations || (listing.lease_terms as any)?.wetLeaseTerms?.thirdPartyOperations}
                    fuelSurchargePolicy={(listing.operational_requirements as any)?.fuelSurchargePolicy || (listing.lease_terms as any)?.wetLeaseTerms?.fuelSurchargePolicy}
                    maintenanceScopeDetailed={(listing.operational_requirements as any)?.maintenanceScopeDetailed || (listing.lease_terms as any)?.wetLeaseTerms?.maintenanceScopeDetailed}
                    geographicLimitations={(listing.operational_requirements as any)?.geographicLimitations || (listing.lease_terms as any)?.wetLeaseTerms?.geographicLimitations}
                    wetLeaseTerms={(listing.lease_terms as any)?.wetLeaseTerms}
                    hidePrice={shouldHidePrice}
                    onRequestInfo={() => requestField('price')}
                  />
                )}

                {listing.listing_type === 'dry_lease' && (
                  <DryLeaseDetailsDisplay
                    hourlyRate={(listing.lease_terms as any)?.hourlyRate}
                    monthlyRate={(listing.lease_terms as any)?.monthlyRate}
                    depositRequired={(listing.lease_terms as any)?.depositRequired}
                    minimumPeriod={(listing.lease_terms as any)?.minimumPeriod || listing.minimum_lease_period}
                    maximumPeriod={(listing.lease_terms as any)?.maximumPeriod || listing.maximum_lease_period}
                    // Availability
                    availability={(listing.lease_terms as any)?.availability}
                    availableFromDate={(listing.lease_terms as any)?.availableFromDate}
                    availableUntilDate={(listing.lease_terms as any)?.availableUntilDate}
                    // Delivery options
                    deliveryOptions={(listing.lease_terms as any)?.deliveryOptions}
                    // Maintenance terms
                    maintenanceResponsibility={(listing.operational_requirements as any)?.dryLeaseTerms?.maintenanceResponsibility || (listing.lease_terms as any)?.dryLeaseTerms?.maintenanceResponsibility}
                    partsRepairsPayment={(listing.operational_requirements as any)?.dryLeaseTerms?.partsAndRepairsPayment || (listing.lease_terms as any)?.dryLeaseTerms?.partsAndRepairsPayment}
                    returnConditions={(listing.operational_requirements as any)?.dryLeaseTerms?.returnConditions || (listing.lease_terms as any)?.dryLeaseTerms?.returnConditions}
                    maxUsageHoursPerMonth={(listing.operational_requirements as any)?.dryLeaseTerms?.maxUsageHoursPerMonth || (listing.lease_terms as any)?.dryLeaseTerms?.maxUsageHoursPerMonth}
                    insuranceCoverageRequired={(listing.operational_requirements as any)?.dryLeaseTerms?.insuranceCoverageRequired || (listing.lease_terms as any)?.dryLeaseTerms?.insuranceCoverageRequired}
                    registrationRequirements={(listing.operational_requirements as any)?.dryLeaseTerms?.registrationRequirements || (listing.lease_terms as any)?.dryLeaseTerms?.registrationRequirements}
                    inspectionRights={(listing.operational_requirements as any)?.dryLeaseTerms?.inspectionRights || (listing.lease_terms as any)?.dryLeaseTerms?.inspectionRights}
                    earlyTerminationFees={(listing.operational_requirements as any)?.dryLeaseTerms?.earlyTerminationFees || (listing.lease_terms as any)?.dryLeaseTerms?.earlyTerminationFees}
                    // Operational requirements
                    pilotQualifications={(listing.operational_requirements as any)?.pilotQualifications}
                    insuranceRequirements={(listing.operational_requirements as any)?.insuranceRequirements}
                    operatingLimitations={(listing.operational_requirements as any)?.operatingLimitations}
                    // Emergency & support
                    emergencyContact={(listing.operational_requirements as any)?.emergencyContact}
                    breakdownProcedures={(listing.operational_requirements as any)?.breakdownProcedures}
                    dryLeaseTerms={(listing.lease_terms as any)?.dryLeaseTerms}
                    hidePrice={shouldHidePrice}
                    onRequestInfo={() => requestField('price')}
                  />
                )}

                {/* Sale Wizard Data Display */}
                <SaleWizardDataDisplay 
                  aircraftDetails={aircraftDetails}
                  fallbackData={{
                    manufacturer: listing.helicopters?.manufacturer,
                    model: listing.helicopters?.model,
                    year: listing.year_manufactured,
                    condition: listing.condition,
                    totalFlightHours: listing.total_flight_hours,
                    price: listing.price_usd,
                    location: humanAddress || listing.location,
                    selectedOptions: listing.selectedOptions,
                    images: listing.images,
                    documents: listing.documents,
                    title: listing.title,
                    description: listing.description,
                    media: listing.media,
                    mediaByCategory: listing.mediaByCategory,
                    latitude: listing.latitude,
                    longitude: listing.longitude,
                    service_locations: (listing as any).service_locations,
                    is_buy_request: listing.is_buy_request,
                    helicopterIconUrl: listing.helicopters?.icon_url,
                    listingId: listing.id,
                    currentUserId: user?.id,
                    sellerId: listing.user_id,
                  }}
                  hideLocation={shouldHideLocation}
                  hideHours={shouldHideHours}
                  hideSerial={shouldHideSerial}
                  humanAddress={humanAddress || undefined}
                />
              </div>
            </div>
          </div>
        </DrawerContent>
        
        {/* Set Hero Image Modal */}
        <SetHeroImageModal
          listingId={listingId}
          open={heroImageModalOpen}
          onOpenChange={setHeroImageModalOpen}
          onSuccess={fetchListingDetails}
        />
        
        {/* Manage Photos Modal */}
        <ManagePhotosModal
          listingId={listingId}
          open={managePhotosModalOpen}
          onOpenChange={setManagePhotosModalOpen}
          onSuccess={fetchListingDetails}
        />
      </Drawer>
    );
  }

  // Desktop version with Dialog
  return (
    <Dialog open={isControlled ? effectiveOpen : undefined} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-7xl h-[90vh] max-h-[90vh] overflow-hidden flex flex-col p-0">
        {/* Header section - Airbnb-style structured layout */}
        <div className="bg-background border-b border-border flex-shrink-0">
          <div className="p-8 space-y-6">
            
            {/* Title Section - Contained zone with subtle background */}
            <div className="bg-muted/30 rounded-lg border border-border p-4">
              <div className="flex items-center gap-3">
                {/* Icon */}
                <div className="w-12 h-12 rounded-full bg-background flex items-center justify-center border border-border shadow-sm flex-shrink-0">
                  <StandardizedIcon 
                    src={listing.helicopters.icon_url ? `${listing.helicopters.icon_url}?v=${Date.now()}` : helicopterFrontViewIcon} 
                    alt={`${listing.helicopters.manufacturer} ${listing.helicopters.model} icon`} 
                    size="sm" 
                    className="text-muted-foreground"
                  />
                </div>
                
                {/* Title and badges */}
                <div className="flex-1 min-w-0">
                <DialogTitle className="text-2xl font-semibold text-foreground mb-2">
                    {listing.helicopters.manufacturer} {listing.helicopters.model}
                  </DialogTitle>
                  <DialogDescription className="sr-only">
                    {getListingTypeLabel(listing.listing_type, listing.is_buy_request, listing.lease_type)} listing for {listing.helicopters.manufacturer} {listing.helicopters.model}
                  </DialogDescription>
                  
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge variant="outline" className="text-xs px-2 py-0.5 font-normal">
                      {getListingTypeLabel(listing.listing_type, listing.is_buy_request, listing.lease_type)}
                    </Badge>
                    <Badge variant="outline" className="text-xs px-2 py-0.5 font-normal">
                      {formatConditionLabel(listing.condition, listing.is_buy_request)}
                    </Badge>
                    {(listing as any).configuration_name && (
                      <Badge 
                        variant="outline" 
                        className="text-sm px-3 py-1.5 font-semibold bg-gradient-to-r from-primary/15 to-primary/5 border-2 border-primary/30 text-primary flex items-center gap-1.5 shadow-sm"
                      >
                        <Sparkles className="w-4 h-4" />
                        {(listing as any).configuration_name}
                      </Badge>
                    )}
                    {listing.featured && (
                      <span className="text-xs text-muted-foreground flex items-center gap-1">
                        <Star className="w-3 h-3 fill-current" />
                        Featured
                      </span>
                    )}
                  </div>
                  
                  {/* Title and Description */}
                  {listing.title && (
                    <div className="mt-3 text-foreground text-lg font-medium">
                      {listing.title}
                    </div>
                  )}
                  {listing.description && (
                    <p className="mt-1.5 text-muted-foreground text-sm line-clamp-3">
                      {listing.description}
                    </p>
                  )}
                  
                  {/* Seller Section */}
                  {listing.profiles && (
                    <SellerSection profile={listing.profiles} compact className="mt-3" onNavigate={() => handleOpenChange(false)} />
                  )}
                </div>
                
                {/* Add hero image button for buy requests with no photos */}
                {listing.is_buy_request && !heroImageUrl && user?.id === listing.user_id && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setHeroImageModalOpen(true)}
                    className="flex-shrink-0"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    Add Hero Image
                  </Button>
                )}
                
                {/* Add photos button for sale/lease listings with no photos */}
                {!listing.is_buy_request && 
                  !listing.media?.length && 
                  (!listing.images || listing.images.length === 0) && 
                  user?.id === listing.user_id && (
                  <Button
                    variant="default"
                    size="sm"
                    onClick={() => setManagePhotosModalOpen(true)}
                    className="flex-shrink-0"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    Add Photos
                  </Button>
                )}
              </div>
            </div>

            {/* Metadata Bar - Separated zone with borders */}
            <div className="flex items-center gap-6 py-4 border-t border-b border-border bg-background">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <MapPin className="w-4 h-4 text-muted-foreground/60" />
                {shouldHideLocation ? (
                  <BlurredPrivateData
                    placeholderContent={<span className="font-medium">City, Country</span>}
                    fieldName="Location"
                    size="sm"
                    inline
                    onRequestInfo={() => requestField('location')}
                  />
                ) : (
                  <span className="font-medium">{humanAddress || listing.location}</span>
                )}
              </div>
              
              {/* Year or Max Age */}
              {listing.is_buy_request ? (
                (listing as any).max_age && (
                  <>
                    <div className="w-px h-4 bg-border" />
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Calendar className="w-4 h-4 text-muted-foreground/60" />
                      <span>Up to {(listing as any).max_age} years old</span>
                    </div>
                  </>
                )
              ) : (
                (aircraftDetails.year ?? listing.year_manufactured) && (
                  <>
                    <div className="w-px h-4 bg-border" />
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Calendar className="w-4 h-4 text-muted-foreground/60" />
                      <span>{aircraftDetails.year ?? listing.year_manufactured}</span>
                    </div>
                  </>
                )
              )}

              {/* Total Hours or Max Hours */}
              {listing.is_buy_request ? (
                (listing as any).max_flight_hours && (
                  <>
                    <div className="w-px h-4 bg-border" />
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Clock className="w-4 h-4 text-muted-foreground/60" />
                      <span>Max {Number((listing as any).max_flight_hours).toLocaleString()} hrs</span>
                    </div>
                  </>
                )
              ) : (
                (aircraftDetails.totalFlightHours ?? listing.total_flight_hours) && (
                  <>
                    <div className="w-px h-4 bg-border" />
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Clock className="w-4 h-4 text-muted-foreground/60" />
                      {shouldHideHours ? (
                        <BlurredPrivateData
                          placeholderContent={<span>X,XXX hrs</span>}
                          fieldName="Hours"
                          size="sm"
                          inline
                          onRequestInfo={() => requestField('hours')}
                        />
                      ) : (
                        <span>{(aircraftDetails.totalFlightHours ?? listing.total_flight_hours).toLocaleString()} hrs</span>
                      )}
                    </div>
                  </>
                )
              )}
            </div>

            {/* Price & Actions Section - Contained zone with background */}
            <div className="bg-muted/50 rounded-lg border border-border p-4">
              <div className="flex items-center justify-between gap-6">
                {/* Price/Budget section */}
                <div>
                  <p className="text-sm text-muted-foreground mb-1">{listing.is_buy_request ? 'Budget' : 'Price'}</p>
                  {shouldHidePrice ? (
                    <BlurredPrivateData
                      placeholderContent={<span className="text-3xl font-semibold text-foreground">$X,XXX,XXX</span>}
                      fieldName="Price"
                      size="lg"
                      onRequestInfo={() => requestField('price')}
                    />
                  ) : (
                    <p className="text-3xl font-semibold text-foreground">
                      {listing.is_buy_request ? (
                        (() => {
                          const min = listing.budget_min;
                          const max = listing.budget_max;
                          if (min && max) return `${formatPrice(min, listing.listing_type)} - ${formatPrice(max, listing.listing_type)}`;
                          if (min) return `From ${formatPrice(min, listing.listing_type)}`;
                          if (max) return `Up to ${formatPrice(max, listing.listing_type)}`;
                          return 'Budget not specified';
                        })()
                      ) : (
                        listing.price_usd != null ? formatPrice(listing.price_usd, listing.listing_type) : 'Price on request'
                      )}
                    </p>
                  )}
                </div>
                
                {/* Actions section */}
                <div className="flex items-center gap-2 flex-shrink-0">
                  <Button 
                    className={cn(isOwner ? "bg-muted text-muted-foreground cursor-not-allowed hover:bg-muted" : "")}
                    onClick={isOwner ? undefined : openContactSeller}
                    disabled={isOwner}
                  >
                    <MessageCircle className="w-4 h-4 mr-2" />
                    {isOwner ? 'This is your listing' : listing.is_buy_request ? 'Contact Buyer' : 'Contact Seller'}
                  </Button>
                  
                  {listing.is_buy_request && (
                    <Button 
                      variant="outline"
                      onClick={(e) => {
                        e.stopPropagation();
                        // Dispatch custom event to open BuyRequestListingResponse modal
                        const event = new CustomEvent('open-buy-request-response', { detail: { request: listing } });
                        window.dispatchEvent(event);
                      }}
                    >
                      <DollarSign className="w-4 h-4 mr-2" />
                      Offer Your Helicopter
                    </Button>
                  )}
                  
                  <Button 
                    variant="outline" 
                    size="icon" 
                    onClick={handleFavoriteToggle}
                    aria-label={listing && isFavorite(listing.id) ? 'Remove from favorites' : 'Add to favorites'}
                  >
                    <Heart className={cn("w-4 h-4", listing && isFavorite(listing.id) && "fill-red-500 text-red-500")} />
                  </Button>
                  
                  {listing && (
                    <ShareListingButton
                      listingId={listing.id}
                      title={listing.title}
                      price={listing.price_usd || 0}
                      location={listing.location}
                      listingType={listing.listing_type}
                      variant="outline"
                      size="icon"
                    />
                  )}
                </div>
              </div>
            </div>

          </div>
        </div>

        <ScrollArea ref={desktopScrollRef} className="flex-1 min-h-0">
          <div className="p-8 space-y-8 overflow-x-hidden">
            {/* Professional Photo Gallery */}
            {listing.is_buy_request && heroImageUrl ? (
              // For buy requests with hero image, show it prominently
              <Card className="overflow-hidden">
                <div className="aspect-[16/10] relative bg-muted">
                  <OptimizedImage
                    src={heroImageUrl}
                    alt={`${listing.helicopters.manufacturer} ${listing.helicopters.model} hero image`}
                    size="full"
                    priority
                    className="w-full h-full object-contain"
                  />
                </div>
                <CardContent className="p-4">
                  <p className="text-sm text-muted-foreground">Hero Image</p>
                </CardContent>
              </Card>
            ) : (
              // Otherwise, use the professional gallery
              <ProfessionalPhotoGallery 
                media={listing.media || []} 
                legacyImages={listing.images || []}
                canManagePhotos={user?.id === listing.user_id && !listing.is_buy_request}
                onAddPhotos={() => setManagePhotosModalOpen(true)}
              />
            )}

            {/* Premium Features Section */}
            {premiumOptions.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Sparkles className="w-5 h-5 text-yellow-500" />
                    Premium Features
                  </CardTitle>
                  <p className="text-sm text-muted-foreground">
                    High-value equipment that significantly enhances market value
                  </p>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {premiumOptions.map((option) => (
                      <div key={option.id} className="premium-option-badge">
                        <OptionIcon option={option} size={16} showLabel={false} />
                        <div>
                          <div className="font-medium text-sm">{option.name}</div>
                          {option.value_add_percent && (
                            <div className="text-xs text-green-600 dark:text-green-400">+{option.value_add_percent}%</div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Categorized Equipment & Options */}
            {listing.selectedOptions && listing.selectedOptions.length > 0 && (
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-lg">Equipment & Options</CardTitle>
                      <p className="text-sm text-muted-foreground mt-1">
                        {listing.selectedOptions.length} items included
                      </p>
                    </div>
                    <Badge variant="secondary" className="text-lg">
                      {listing.selectedOptions.length}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <Tabs defaultValue={Object.keys(categorizedOptions)[0]} className="w-full">
                    <TabsList className="w-full flex-wrap h-auto gap-2 bg-muted/50 overflow-x-auto scrollbar-hide after:content-[''] after:w-4 after:flex-shrink-0">
                      {Object.keys(categorizedOptions).map((category) => (
                        <TabsTrigger 
                          key={category} 
                          value={category}
                          className="flex items-center gap-2"
                        >
                          <CategoryIconDisplay category={category} className="w-4 h-4" />
                          {category} ({categorizedOptions[category].length})
                        </TabsTrigger>
                      ))}
                    </TabsList>
                    {Object.entries(categorizedOptions).map(([category, options]: [string, Option[]]) => (
                      <TabsContent key={category} value={category} className="mt-4">
                        <div className="options-category-grid">
                          {options.map((option) => (
                            <div key={option.id} className="option-item-compact">
                              <OptionIcon option={option} size={16} showLabel={false} />
                              <div className="min-w-0 flex-1">
                                <div className="text-sm font-medium truncate">{option.name}</div>
                                {option.description && (
                                  <div className="text-xs text-muted-foreground line-clamp-2">
                                    {option.description}
                                  </div>
                                )}
                                {option.value_add_percent && (
                                  <Badge variant="secondary" className="text-xs mt-1">
                                    +{option.value_add_percent}% value
                                  </Badge>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </TabsContent>
                    ))}
                  </Tabs>
                </CardContent>
              </Card>
            )}

            {/* Listing-Type Specific Components */}
            {listing.is_buy_request && (
              <BuyRequestFieldsDisplay
                budgetMin={listing.budget_min ?? undefined}
                budgetMax={listing.budget_max ?? undefined}
                financingStatus={(listing as any).financing_status}
                urgencyLevel={(listing as any).urgency_level}
                preferredDeliveryDate={(listing as any).preferred_delivery_date}
                modelPreferences={(listing as any).model_preferences}
                requiredEquipment={(listing as any).required_equipment}
                downPayment={(listing as any).down_payment}
                loanTerm={(listing as any).loan_term}
                maxAge={(listing as any).max_age}
                maxFlightHours={(listing as any).max_flight_hours}
                operationalCoverage={(listing.operational_requirements as any)?.operationalCoverage}
                searchRadius={(listing as any).search_radius}
                bestTimeToCall={(listing as any).best_time_to_call}
                insuranceCapability={(listing.operational_requirements as any)?.insuranceCapability}
                pilotQualifications={(listing.operational_requirements as any)?.pilotQualifications}
                serviceLevel={(listing.operational_requirements as any)?.serviceLevel || (listing.operational_requirements as any)?.wetLeaseTerms?.serviceLevel}
                responseTime={(listing.operational_requirements as any)?.responseTime || (listing.operational_requirements as any)?.wetLeaseTerms?.responseTime}
                operatingHours={(listing.operational_requirements as any)?.operatingHours || (listing.operational_requirements as any)?.buyRequestDetails?.operatingHours}
                intendedUse={(listing.operational_requirements as any)?.intendedUse || (listing.operational_requirements as any)?.buyRequestDetails?.intendedUse}
                minimumBilling={(listing.operational_requirements as any)?.minimumBilling || (listing.operational_requirements as any)?.buyRequestDetails?.minimumBilling}
                preferredContactMethods={(listing as any).preferred_contact_methods || []}
                requestedServices={(listing.included_services as any) || (listing.operational_requirements as any)?.selectedServices || []}
                budgetFlexibility={(listing.operational_requirements as any)?.buyRequestDetails?.budgetFlexibility}
                preApprovalAmount={(listing.operational_requirements as any)?.buyRequestDetails?.preApprovalAmount}
                preferredDeliveryMethod={(listing.operational_requirements as any)?.buyRequestDetails?.preferredDeliveryMethod}
                tradeInAircraft={(listing.operational_requirements as any)?.buyRequestDetails?.tradeInAircraft}
                minCondition={(listing as any).condition}
                configurationName={(listing as any).configuration_name}
              />
            )}

            {listing.listing_type === 'rent' && (
              <RentDetailsDisplay
                hourlyRate={(listing as any).hourly_rate}
                dailyRate={(listing as any).daily_rate}
                minimumRentalPeriod={(listing as any).minimum_rental_period}
                securityDeposit={(listing as any).security_deposit}
                pilotRequirements={(listing as any).pilot_requirements}
                insuranceRequirements={(listing as any).insurance_requirements}
                geographicRestrictions={(listing as any).geographic_restrictions}
                weatherMinimums={(listing as any).weather_minimums}
                checkoutRequirements={(listing as any).checkout_requirements}
                hidePrice={shouldHidePrice}
                onRequestInfo={() => requestField('price')}
              />
            )}

            {(listing.listing_type === 'dry_lease' || listing.listing_type === 'wet_lease') && (
              <ServiceIconsDisplay
                services={listing.included_services}
                leaseType={listing.lease_type as 'dry' | 'wet'}
                crewQualifications={(listing.operational_requirements as any)?.crewQualifications}
                maintenanceResponsibility={(listing.lease_terms as any)?.maintenanceResponsibility}
                minimumLeasePeriod={listing.minimum_lease_period}
                maximumLeasePeriod={listing.maximum_lease_period}
                positioningCost={(listing.lease_terms as any)?.positioningCost}
              />
            )}

            {listing.listing_type === 'wet_lease' && (
              <WetLeaseDetailsDisplay
                hourlyRate={(listing.lease_terms as any)?.hourlyRate}
                monthlyRate={(listing.lease_terms as any)?.monthlyRate}
                depositRequired={(listing.lease_terms as any)?.depositRequired}
                minimumPeriod={(listing.lease_terms as any)?.minimumPeriod || listing.minimum_lease_period}
                maximumPeriod={(listing.lease_terms as any)?.maximumPeriod || listing.maximum_lease_period}
                crewQualifications={(listing.operational_requirements as any)?.crewQualifications || (listing.lease_terms as any)?.wetLeaseTerms?.crewQualifications}
                standbyRates={(listing.operational_requirements as any)?.standbyRates || (listing.lease_terms as any)?.wetLeaseTerms?.standbyRates}
                positioningCosts={(listing.operational_requirements as any)?.positioningCosts || (listing.lease_terms as any)?.wetLeaseTerms?.positioningCosts}
                minimumBilling={(listing.operational_requirements as any)?.minimumBilling || (listing.lease_terms as any)?.wetLeaseTerms?.minimumBilling}
                operationalControl={(listing.operational_requirements as any)?.operationalControl || (listing.lease_terms as any)?.wetLeaseTerms?.operationalControl}
                thirdPartyOperations={(listing.operational_requirements as any)?.thirdPartyOperations || (listing.lease_terms as any)?.wetLeaseTerms?.thirdPartyOperations}
                fuelSurchargePolicy={(listing.operational_requirements as any)?.fuelSurchargePolicy || (listing.lease_terms as any)?.wetLeaseTerms?.fuelSurchargePolicy}
                maintenanceScopeDetailed={(listing.operational_requirements as any)?.maintenanceScopeDetailed || (listing.lease_terms as any)?.wetLeaseTerms?.maintenanceScopeDetailed}
                geographicLimitations={(listing.operational_requirements as any)?.geographicLimitations || (listing.lease_terms as any)?.wetLeaseTerms?.geographicLimitations}
                wetLeaseTerms={(listing.lease_terms as any)?.wetLeaseTerms}
                hidePrice={shouldHidePrice}
                onRequestInfo={() => requestField('price')}
              />
            )}

            {listing.listing_type === 'dry_lease' && (
              <DryLeaseDetailsDisplay
                hourlyRate={(listing.lease_terms as any)?.hourlyRate}
                monthlyRate={(listing.lease_terms as any)?.monthlyRate}
                depositRequired={(listing.lease_terms as any)?.depositRequired}
                minimumPeriod={(listing.lease_terms as any)?.minimumPeriod || listing.minimum_lease_period}
                maximumPeriod={(listing.lease_terms as any)?.maximumPeriod || listing.maximum_lease_period}
                availability={(listing.lease_terms as any)?.availability}
                availableFromDate={(listing.lease_terms as any)?.availableFromDate}
                availableUntilDate={(listing.lease_terms as any)?.availableUntilDate}
                deliveryOptions={(listing.lease_terms as any)?.deliveryOptions}
                maintenanceResponsibility={(listing.operational_requirements as any)?.dryLeaseTerms?.maintenanceResponsibility || (listing.lease_terms as any)?.dryLeaseTerms?.maintenanceResponsibility}
                partsRepairsPayment={(listing.operational_requirements as any)?.dryLeaseTerms?.partsAndRepairsPayment || (listing.lease_terms as any)?.dryLeaseTerms?.partsAndRepairsPayment}
                returnConditions={(listing.operational_requirements as any)?.dryLeaseTerms?.returnConditions || (listing.lease_terms as any)?.dryLeaseTerms?.returnConditions}
                maxUsageHoursPerMonth={(listing.operational_requirements as any)?.dryLeaseTerms?.maxUsageHoursPerMonth || (listing.lease_terms as any)?.dryLeaseTerms?.maxUsageHoursPerMonth}
                insuranceCoverageRequired={(listing.operational_requirements as any)?.dryLeaseTerms?.insuranceCoverageRequired || (listing.lease_terms as any)?.dryLeaseTerms?.insuranceCoverageRequired}
                registrationRequirements={(listing.operational_requirements as any)?.dryLeaseTerms?.registrationRequirements || (listing.lease_terms as any)?.dryLeaseTerms?.registrationRequirements}
                inspectionRights={(listing.operational_requirements as any)?.dryLeaseTerms?.inspectionRights || (listing.lease_terms as any)?.dryLeaseTerms?.inspectionRights}
                earlyTerminationFees={(listing.operational_requirements as any)?.dryLeaseTerms?.earlyTerminationFees || (listing.lease_terms as any)?.dryLeaseTerms?.earlyTerminationFees}
                pilotQualifications={(listing.operational_requirements as any)?.pilotQualifications}
                insuranceRequirements={(listing.operational_requirements as any)?.insuranceRequirements}
                operatingLimitations={(listing.operational_requirements as any)?.operatingLimitations}
                emergencyContact={(listing.operational_requirements as any)?.emergencyContact}
                breakdownProcedures={(listing.operational_requirements as any)?.breakdownProcedures}
                dryLeaseTerms={(listing.lease_terms as any)?.dryLeaseTerms}
                hidePrice={shouldHidePrice}
                onRequestInfo={() => requestField('price')}
              />
            )}

            {/* Sale Wizard Data Display */}
            <SaleWizardDataDisplay 
              aircraftDetails={aircraftDetails}
              fallbackData={{
                manufacturer: listing.helicopters?.manufacturer,
                model: listing.helicopters?.model,
                year: listing.year_manufactured,
                condition: listing.condition,
                totalFlightHours: listing.total_flight_hours,
                price: listing.price_usd,
                location: humanAddress || listing.location,
                selectedOptions: listing.selectedOptions,
                images: listing.images,
                documents: listing.documents,
                title: listing.title,
                description: listing.description,
                media: listing.media,
                mediaByCategory: listing.mediaByCategory,
                latitude: listing.latitude,
                longitude: listing.longitude,
                service_locations: (listing as any).service_locations,
                is_buy_request: listing.is_buy_request,
                helicopterIconUrl: listing.helicopters?.icon_url,
                listingId: listing.id,
                currentUserId: user?.id,
                sellerId: listing.user_id,
              }}
              hideLocation={shouldHideLocation}
              hideHours={shouldHideHours}
              hideSerial={shouldHideSerial}
              humanAddress={humanAddress || undefined}
            />
          </div>
        </ScrollArea>
        </DialogContent>
        
        {/* Set Hero Image Modal */}
        <SetHeroImageModal
          listingId={listingId}
          open={heroImageModalOpen}
          onOpenChange={setHeroImageModalOpen}
          onSuccess={fetchListingDetails}
        />
        
        {/* Manage Photos Modal */}
        <ManagePhotosModal
          listingId={listingId}
          open={managePhotosModalOpen}
          onOpenChange={setManagePhotosModalOpen}
          onSuccess={fetchListingDetails}
        />
      </Dialog>
    );
  };

export default ListingDetailModal;
